import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPage.css';

const PrivacyPolicy: React.FC = () => {
    return (
        <div className="policy-page">
            <nav className="policy-nav">
                <Link to="/" className="logo">Commerium</Link>
            </nav>

            <main className="policy-content">
                <h1>Privacy Policy</h1>
                <p className="last-updated">Last updated: January 20, 2026</p>

                <section>
                    <h2>1. Introduction</h2>
                    <p>
                        Welcome to Commerium ("we," "our," or "us"). We are committed to protecting your personal
                        information and your right to privacy. This Privacy Policy explains how we collect, use,
                        disclose, and safeguard your information when you use our platform.
                    </p>
                    <p>
                        Please read this privacy policy carefully. If you do not agree with the terms of this
                        privacy policy, please do not access the platform.
                    </p>
                </section>

                <section>
                    <h2>2. Information We Collect</h2>
                    <h3>2.1 Personal Information</h3>
                    <p>We may collect personal information that you voluntarily provide when you:</p>
                    <ul>
                        <li>Register for an account</li>
                        <li>Connect third-party marketplace accounts (Etsy, Shopify, Amazon, etc.)</li>
                        <li>Use our AI listing generation features</li>
                        <li>Contact our support team</li>
                        <li>Subscribe to our newsletter</li>
                    </ul>
                    <p>This information may include:</p>
                    <ul>
                        <li>Name and email address</li>
                        <li>Business name and address</li>
                        <li>Payment information (processed securely via Shopify)</li>
                        <li>Product images and descriptions</li>
                        <li>Marketplace API credentials (encrypted)</li>
                    </ul>

                    <h3>2.2 Automatically Collected Information</h3>
                    <p>When you access our platform, we automatically collect:</p>
                    <ul>
                        <li>Device information (browser type, operating system)</li>
                        <li>IP address and approximate location</li>
                        <li>Usage data (pages visited, features used, time spent)</li>
                        <li>Cookies and similar tracking technologies</li>
                    </ul>
                </section>

                <section>
                    <h2>3. How We Use Your Information</h2>
                    <p>We use your information to:</p>
                    <ul>
                        <li>Provide, operate, and maintain our platform</li>
                        <li>Process AI-generated listings and sync with marketplaces</li>
                        <li>Process payments and send transaction confirmations</li>
                        <li>Send administrative information (updates, security alerts)</li>
                        <li>Respond to inquiries and provide customer support</li>
                        <li>Improve our AI models and platform features</li>
                        <li>Detect and prevent fraud or unauthorized access</li>
                        <li>Comply with legal obligations</li>
                    </ul>
                </section>

                <section>
                    <h2>4. AI and Data Processing</h2>
                    <p>
                        Our platform uses artificial intelligence to analyze product images and generate listing
                        content. When you upload images or product information:
                    </p>
                    <ul>
                        <li>Images are processed by our AI systems (powered by Google Gemini)</li>
                        <li>Generated content is stored in your account</li>
                        <li>We do not use your product images to train third-party AI models</li>
                        <li>You retain full ownership of your product images and data</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Data Sharing and Disclosure</h2>
                    <p>We may share your information with:</p>
                    <ul>
                        <li><strong>Connected Marketplaces:</strong> When you authorize connections to Etsy, Shopify, etc.</li>
                        <li><strong>Service Providers:</strong> Cloud hosting (Render, Vercel), payment processing (Shopify),
                            analytics (Plausible)</li>
                        <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                    </ul>
                    <p>We do not sell your personal information to third parties.</p>
                </section>

                <section>
                    <h2>6. Data Security</h2>
                    <p>
                        We implement industry-standard security measures including:
                    </p>
                    <ul>
                        <li>AES-256 encryption for data at rest</li>
                        <li>TLS 1.3 for data in transit</li>
                        <li>Regular security audits and penetration testing</li>
                        <li>Secure credential storage with hashing</li>
                        <li>Role-based access controls</li>
                    </ul>
                </section>

                <section>
                    <h2>7. Data Retention</h2>
                    <p>
                        We retain your personal information for as long as your account is active or as needed to
                        provide services. Upon account deletion, we will delete or anonymize your data within 30 days,
                        except where retention is required by law.
                    </p>
                </section>

                <section>
                    <h2>8. Your Rights (GDPR)</h2>
                    <p>If you are in the European Economic Area, you have the right to:</p>
                    <ul>
                        <li><strong>Access:</strong> Request a copy of your personal data</li>
                        <li><strong>Rectification:</strong> Correct inaccurate data</li>
                        <li><strong>Erasure:</strong> Request deletion of your data</li>
                        <li><strong>Portability:</strong> Receive your data in a structured format</li>
                        <li><strong>Restriction:</strong> Limit how we process your data</li>
                        <li><strong>Objection:</strong> Object to certain processing activities</li>
                    </ul>
                    <p>To exercise these rights, contact us at privacy@trycommerium.com</p>
                </section>

                <section>
                    <h2>9. International Transfers</h2>
                    <p>
                        Your data may be transferred to and processed in countries outside your residence. We ensure
                        appropriate safeguards (Standard Contractual Clauses) are in place for such transfers.
                    </p>
                </section>

                <section>
                    <h2>10. Children's Privacy</h2>
                    <p>
                        Our platform is not intended for users under 16 years of age. We do not knowingly collect
                        personal information from children.
                    </p>
                </section>

                <section>
                    <h2>11. Changes to This Policy</h2>
                    <p>
                        We may update this privacy policy from time to time. We will notify you of significant
                        changes by email or through a notice on our platform. Your continued use after changes
                        constitutes acceptance.
                    </p>
                </section>

                <section>
                    <h2>12. Contact Us</h2>
                    <p>If you have questions about this Privacy Policy, contact us at:</p>
                    <ul>
                        <li>Email: privacy@trycommerium.com</li>
                        <li>Address: Commerium Technologies, Calle Mayor 1, 28001 Madrid, Spain</li>
                    </ul>
                </section>
            </main>

            <footer className="policy-footer">
                <Link to="/">← Back to Home</Link>
                <p>© 2026 Commerium. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
